import React, { useState, useEffect, useContext, useMemo } from 'react';
import axios from 'axios';
import { AuthContext } from '../../components/AuthContext';
import Select from 'react-select';
import Swal from 'sweetalert2';
import { debounce } from 'lodash';
import { 
  FaCalculator,
  FaSort,
  FaSortUp,
  FaSortDown,
  FaTimes,
  FaPlus,
  FaDownload,
  FaSearch,
  FaBoxOpen,
  FaEdit,
  FaTrash
} from 'react-icons/fa';

export default function TallyParametersManagement() {
  const { user } = useContext(AuthContext);
  const [parameters, setParameters] = useState([]);
  const [categories, setCategories] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    width: '',
    height: '',
    piecesPerBox: '',
    weightPerBox: '',
    calculationType: 'pcs',
    coverageArea: '',
    coverageAreaUnit: 'sqft'
  });
  const [editId, setEditId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  const [showParameterModal, setShowParameterModal] = useState(false);
  const [darkMode, setDarkMode] = useState(false);

  // Sync dark mode with localStorage
  useEffect(() => {
    const checkDarkMode = () => {
      const isDark = localStorage.getItem('darkMode') === 'true';
      setDarkMode(isDark);
    };
    
    checkDarkMode();
    const interval = setInterval(checkDarkMode, 500);
    return () => clearInterval(interval);
  }, []);

  // Debounced search
  const debouncedSearch = useMemo(() => {
    return debounce((value) => {
      setSearchTerm(value);
      setPage(1);
    }, 500);
  }, []);

  // Fetch initial data
  useEffect(() => {
    console.log('Fetching categories on mount');
    fetchCategories();
  }, []);

  useEffect(() => {
    console.log('Fetching data with params:', { page, limit, searchTerm, sortField, sortOrder });
    fetchData();
  }, [page, limit, searchTerm, sortField, sortOrder]);

  useEffect(() => {
    debouncedSearch(searchInput);
    return () => debouncedSearch.cancel();
  }, [searchInput, debouncedSearch]);

  // Auto-calculate coverage area when width, height, pieces per box, or unit changes
  useEffect(() => {
    if (formData.calculationType === 'pcsPerBox' && formData.width && formData.height && formData.piecesPerBox) {
      const width = parseFloat(formData.width);
      const height = parseFloat(formData.height);
      const piecesPerBox = parseFloat(formData.piecesPerBox);
      
      if (!isNaN(width) && !isNaN(height) && !isNaN(piecesPerBox) && width > 0 && height > 0 && piecesPerBox > 0) {
        // Convert millimeters to inches (1 inch = 25.4 mm)
        const width_inches = width / 25.4;
        const height_inches = height / 25.4;
        
        let baseArea = 0;
        
        if (formData.coverageAreaUnit === 'sqft') {
          // Calculate base area in square feet from width and height (converted from mm to inches)
          baseArea = (width_inches * height_inches) / 144; // Convert sq inches to sq feet
        } else if (formData.coverageAreaUnit === 'sqm') {
          // Calculate base area in square meters from width and height (converted from mm to inches)
          baseArea = (width_inches * height_inches) / 1550; // Convert sq inches to sq meters
        }
        
        // Multiply base area by pieces per box
        const areaPerBox = baseArea * piecesPerBox;
        setFormData(prev => ({
          ...prev,
          coverageArea: areaPerBox.toFixed(2)
        }));
      }
    }
  }, [formData.width, formData.height, formData.piecesPerBox, formData.coverageAreaUnit, formData.calculationType]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page,
        limit,
        search: searchTerm || '',
        sortField: sortField,
        sortOrder: sortOrder
      });
      
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/tally/parameters?${params.toString()}`
      );
      
      console.log('API Response:', response.data);
      
      if (response.data && response.data.success) {
        // Backend returns data as array directly, not data.parameters
        const parametersData = Array.isArray(response.data.data) 
          ? response.data.data 
          : (response.data.data.parameters || []);
        
        setParameters(parametersData);
        setTotal(response.data.total || response.data.pagination?.total || 0);
      } else {
        setParameters([]);
        setTotal(0);
      }
    } catch (error) {
      console.error('Error fetching parameters:', error);
      setParameters([]);
      setTotal(0);
      Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: 'Failed to fetch parameters',
        timer: 3000,
        showConfirmButton: false
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/categories`);
      console.log('Categories Response:', response.data);
      
      // Backend returns array directly, not wrapped in data object
      if (Array.isArray(response.data)) {
        setCategories(response.data);
      } else if (response.data && response.data.success && response.data.data) {
        setCategories(Array.isArray(response.data.data) ? response.data.data : []);
      } else {
        setCategories([]);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      setCategories([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      
      // Check if user is available
      if (!user) {
        Swal.fire({
          icon: 'error',
          title: 'Error!',
          text: 'User information not available. Please refresh and try again.',
          timer: 3000,
          showConfirmButton: false
        });
        setLoading(false);
        return;
      }
      
      const url = editId 
        ? `${process.env.REACT_APP_API_BASE_URL}/tally/parameters/${editId}`
        : `${process.env.REACT_APP_API_BASE_URL}/tally/parameters`;
      
      const method = editId ? 'put' : 'post';
      
      // Add createdBy field for new parameters
      const payload = editId ? formData : { ...formData, createdBy: user.user._id };

      console.log('Submitting parameter:', { method, url, payload, userId: user.user._id });

      const response = await axios[method](url, payload);
      
      if (response.data && response.data.success) {
        Swal.fire({
          icon: 'success',
          title: editId ? 'Updated!' : 'Created!',
          text: `Parameter ${editId ? 'updated' : 'created'} successfully`,
          timer: 2000,
          showConfirmButton: false
        });
        
        setShowParameterModal(false);
        resetForm();
        fetchData();
      }
    } catch (error) {
      console.error('Error saving parameter:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: error.response?.data?.message || 'Failed to save parameter',
        timer: 3000,
        showConfirmButton: false
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = () => {
    if (!user) {
      Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: 'User information not available. Please refresh the page and try again.',
        confirmButtonText: 'Refresh Page',
        showCancelButton: true
      }).then((result) => {
        if (result.isConfirmed) {
          window.location.reload();
        }
      });
      return;
    }
    setShowParameterModal(true);
  };

  const handleEdit = (parameter) => {
    setFormData({
      name: parameter.name || '',
      category: parameter.category?._id || '',
      width: parameter.width || '',
      height: parameter.height || '',
      piecesPerBox: parameter.piecesPerBox || '',
      weightPerBox: parameter.weightPerBox || '',
      calculationType: parameter.calculationType || 'pcs',
      coverageArea: parameter.coverageArea || '',
      coverageAreaUnit: parameter.coverageAreaUnit || 'sqft'
    });
    setEditId(parameter._id);
    setShowParameterModal(true);
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!'
    });
    
    if (result.isConfirmed) {
      try {
        await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/tally/parameters/${id}`);
        
        Swal.fire({
          icon: 'success',
          title: 'Deleted!',
          text: 'Parameter has been deleted',
          timer: 2000,
          showConfirmButton: false
        });
        
        fetchData();
      } catch (error) {
        console.error('Error deleting parameter:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error!',
          text: 'Failed to delete parameter',
          timer: 3000,
          showConfirmButton: false
        });
      }
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      category: '',
      width: '',
      height: '',
      piecesPerBox: '',
      weightPerBox: '',
      calculationType: 'pcs',
      coverageArea: '',
      coverageAreaUnit: 'sqft'
    });
    setEditId(null);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return <FaSort className="text-gray-400" />;
    return sortOrder === 'asc' ? <FaSortUp className="text-blue-500" /> : <FaSortDown className="text-blue-500" />;
  };

  const handleSearchChange = (e) => {
    setSearchInput(e.target.value);
  };

  const calculateCoveragePerBox = (parameter) => {
    if (parameter.calculationType !== 'pcsPerBox') return 0;
    // coverageArea already includes pieces per box multiplication, so return as is
    return parameter.coverageArea.toFixed(2);
  };

  const calculateCoveragePerPiece = (parameter) => {
    if (parameter.calculationType !== 'pcsPerBox') return 0;
    // Calculate coverage per individual piece
    const pieceCoverage = parameter.coverageArea / parameter.piecesPerBox;
    return pieceCoverage.toFixed(2);
  };

  const handleExportParameters = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/tally/parameters/export`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `tally-parameters-${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      Swal.fire({
        icon: 'success',
        title: 'Export Successful!',
        text: 'Parameters have been exported to Excel',
        timer: 2000,
        showConfirmButton: false
      });
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Export Failed!',
        text: 'Error exporting parameters to Excel',
        timer: 3000,
        showConfirmButton: false
      });
    } finally {
      setLoading(false);
    }
  };

  const categoryOptions = (categories || []).map(cat => ({
    value: cat._id,
    label: cat.name
  }));

  const calculationTypeOptions = [
    { value: 'pcs', label: 'Pieces' },
    { value: 'pcsPerBox', label: 'Pieces per Box' }
  ];

  const coverageAreaUnitOptions = [
    { value: 'sqft', label: 'Square Feet' },
    { value: 'sqm', label: 'Square Meters' }
  ];

  // React-Select custom styles for dark mode
  const selectStyles = {
    control: (base) => ({
      ...base,
      backgroundColor: darkMode ? 'rgb(15 23 42)' : 'white',
      borderColor: darkMode ? 'rgb(51 65 85)' : 'rgb(216 180 254)',
      borderWidth: '2px',
      borderRadius: '1rem',
      padding: '0.5rem',
      boxShadow: 'none',
      '&:hover': {
        borderColor: darkMode ? 'rgb(59 130 246)' : 'rgb(168 85 247)'
      }
    }),
    menu: (base) => ({
      ...base,
      backgroundColor: darkMode ? 'rgb(30 41 59)' : 'white',
      border: darkMode ? '2px solid rgb(51 65 85)' : '2px solid rgb(216 180 254)',
      borderRadius: '1rem',
      overflow: 'hidden'
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isFocused 
        ? (darkMode ? 'rgb(51 65 85)' : 'rgb(243 232 255)')
        : (darkMode ? 'rgb(30 41 59)' : 'white'),
      color: darkMode ? 'rgb(229 231 235)' : 'rgb(17 24 39)',
      fontWeight: '600',
      '&:hover': {
        backgroundColor: darkMode ? 'rgb(51 65 85)' : 'rgb(243 232 255)'
      }
    }),
    singleValue: (base) => ({
      ...base,
      color: darkMode ? 'rgb(229 231 235)' : 'rgb(17 24 39)',
      fontWeight: '600'
    }),
    placeholder: (base) => ({
      ...base,
      color: darkMode ? 'rgb(100 116 139)' : 'rgb(192 132 252)'
    }),
    input: (base) => ({
      ...base,
      color: darkMode ? 'white' : 'rgb(17 24 39)'
    })
  };

  return (
    <>
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { transform: translateY(20px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        @keyframes shimmer {
          0% { background-position: -1000px 0; }
          100% { background-position: 1000px 0; }
        }
        @keyframes gradientShift {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }
        @keyframes glow {
          0%, 100% { box-shadow: 0 0 20px rgba(59, 130, 246, 0.5); }
          50% { box-shadow: 0 0 30px rgba(59, 130, 246, 0.8), 0 0 40px rgba(59, 130, 246, 0.4); }
        }
        .card-hover {
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .card-hover:hover {
          transform: translateY(-8px) scale(1.02);
        }
        .animated-gradient {
          background: linear-gradient(-45deg, #667eea, #764ba2, #f093fb, #4facfe);
          background-size: 400% 400%;
          animation: gradientShift 15s ease infinite;
        }
      `}</style>

      {/* Main Container */}
      <div className={`min-h-screen transition-all duration-500 ${
        darkMode 
          ? 'bg-gradient-to-br from-slate-950 via-blue-950 to-slate-950' 
          : 'bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50'
      }`}>
        <div className="w-full px-4 sm:px-6 lg:px-8 py-10">
          
          {/* Header Card with Animated Gradient */}
          <div className={`relative overflow-hidden border rounded-3xl shadow-2xl px-8 py-8 mb-10 transition-all duration-500 group ${
            darkMode 
              ? 'bg-gradient-to-r from-slate-900/95 via-blue-900/90 to-purple-900/95 border-blue-700/30' 
              : 'bg-white/80 backdrop-blur-xl border-white/60 shadow-purple-200/50'
          }`}>
            {/* Animated background gradient */}
            <div className={`absolute inset-0 opacity-20 transition-opacity duration-500 group-hover:opacity-30 ${
              darkMode ? 'animated-gradient' : 'bg-gradient-to-r from-purple-200 via-pink-200 to-blue-200'
            }`}></div>
            
            <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div className="flex items-center gap-5">
                <div className={`p-4 rounded-2xl shadow-lg transition-all duration-300 group-hover:scale-110 ${
                  darkMode 
                    ? 'bg-gradient-to-br from-blue-600 to-purple-600 shadow-blue-500/50' 
                    : 'bg-gradient-to-br from-indigo-500 to-purple-600 shadow-purple-500/30'
                }`}>
                  <FaCalculator className="text-3xl text-white" />
                </div>
                <div>
                  <h1 className={`text-4xl font-extrabold tracking-tight ${
                    darkMode 
                      ? 'text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400' 
                      : 'text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600'
                  }`}>
                    Tally Parameters
                  </h1>
                  <p className={`text-sm mt-2 font-medium ${
                    darkMode ? 'text-blue-300/90' : 'text-purple-700/80'
                  }`}>
                    ✨ Manage calculation parameters 
                  </p>
                </div>
              </div>
              <button
                onClick={handleExportParameters}
                className={`relative overflow-hidden flex items-center gap-3 px-8 py-4 font-bold rounded-2xl transition-all duration-300 transform hover:scale-105 active:scale-95 shadow-xl hover:shadow-2xl ${
                  darkMode
                    ? 'bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-500 hover:to-green-500 text-white shadow-emerald-500/50'
                    : 'bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white shadow-green-500/30'
                }`}
              >
                <FaDownload className="text-xl" />
                <span>Export to Excel</span>
              </button>
            </div>
          </div>

          {/* Search and Controls - Enhanced */}
          <div className={`relative overflow-hidden border rounded-3xl shadow-xl px-8 py-6 mb-8 transition-all duration-500 ${
            darkMode 
              ? 'bg-gradient-to-r from-slate-900/90 via-slate-800/80 to-slate-900/90 border-slate-700/50 shadow-blue-900/20' 
              : 'bg-white/90 backdrop-blur-xl border-purple-200/60 shadow-purple-200/30'
          }`}>
            <div className="flex flex-col md:flex-row gap-6 items-center justify-between">
              <div className="relative flex-1 w-full md:max-w-xl group">
                <div className={`absolute inset-0 rounded-2xl blur-lg opacity-0 group-hover:opacity-100 transition-opacity duration-500 ${
                  darkMode ? 'bg-blue-500/20' : 'bg-purple-500/20'
                }`}></div>
                <FaSearch className={`absolute left-5 top-1/2 transform -translate-y-1/2 text-lg transition-colors z-10 ${
                  darkMode ? 'text-blue-400 group-hover:text-blue-300' : 'text-purple-500 group-hover:text-purple-600'
                }`} />
                <input
                  type="text"
                  placeholder="🔍 Search parameters by name, category, or type..."
                  value={searchInput}
                  onChange={handleSearchChange}
                  className={`relative w-full pl-14 pr-5 py-4 rounded-2xl border-2 font-medium transition-all duration-300 focus:outline-none focus:ring-4 ${
                    darkMode
                      ? 'bg-slate-950/50 border-slate-700 text-white placeholder-slate-500 focus:border-blue-500 focus:ring-blue-500/30 focus:bg-slate-900/70'
                      : 'bg-white border-purple-200 text-gray-900 placeholder-purple-400/70 focus:border-purple-500 focus:ring-purple-500/20 focus:bg-purple-50/30'
                  }`}
                />
              </div>
              <div className="flex items-center gap-4">
                <label className={`text-sm font-bold ${
                  darkMode ? 'text-blue-300' : 'text-purple-700'
                }`}>
                  Show:
                </label>
                <select
                  value={limit}
                  onChange={(e) => {
                    setLimit(Number(e.target.value));
                    setPage(1);
                  }}
                  className={`px-5 py-3 rounded-xl border-2 font-bold transition-all duration-300 focus:outline-none focus:ring-4 cursor-pointer ${
                    darkMode
                      ? 'bg-slate-950/50 border-slate-700 text-white focus:border-blue-500 focus:ring-blue-500/30 hover:bg-slate-900'
                      : 'bg-white border-purple-200 text-gray-900 focus:border-purple-500 focus:ring-purple-500/20 hover:bg-purple-50'
                  }`}
                >
                  <option value={10}>10 per page</option>
                  <option value={25}>25 per page</option>
                  <option value={50}>50 per page</option>
                  <option value={100}>100 per page</option>
                </select>
              </div>
            </div>
          </div>

          {/* Parameters List Section - Premium Style */}
          <div className={`relative overflow-hidden border rounded-3xl shadow-2xl transition-all duration-500 ${
            darkMode 
              ? 'bg-gradient-to-b from-slate-900/95 to-slate-900/90 border-slate-700/50 shadow-blue-900/20' 
              : 'bg-white/90 backdrop-blur-xl border-purple-200/60 shadow-purple-200/30'
          }`}>
            
            {/* Section Header with Gradient */}
            <div className={`px-8 py-6 border-b backdrop-blur-sm flex items-center justify-between ${
              darkMode 
                ? 'bg-gradient-to-r from-blue-900/30 to-purple-900/30 border-slate-700/50' 
                : 'bg-gradient-to-r from-purple-50/80 to-pink-50/80 border-purple-200/50'
            }`}>
              <div>
                <h2 className={`text-2xl font-extrabold ${
                  darkMode 
                    ? 'text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400' 
                    : 'text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600'
                }`}>
                  Parameters Collection
                </h2>
                <p className={`text-sm mt-1.5 font-semibold ${
                  darkMode ? 'text-blue-300/80' : 'text-purple-600/80'
                }`}>
                  📊 Showing <span className={darkMode ? 'text-blue-400' : 'text-purple-700'}>{(parameters || []).length}</span> of{' '}
                  <span className={darkMode ? 'text-blue-400' : 'text-purple-700'}>{total || 0}</span> parameters
                </p>
              </div>
              <button
                onClick={() => handleSort('name')}
                className={`flex items-center gap-3 px-6 py-3 rounded-xl font-bold transition-all duration-300 transform hover:scale-105 active:scale-95 shadow-lg ${
                  darkMode
                    ? 'bg-gradient-to-r from-slate-700 to-slate-600 hover:from-slate-600 hover:to-slate-500 text-blue-300 shadow-blue-900/30'
                    : 'bg-gradient-to-r from-purple-100 to-pink-100 hover:from-purple-200 hover:to-pink-200 text-purple-700 shadow-purple-500/20'
                }`}
              >
                <span>Sort</span>
                {getSortIcon('name')}
              </button>
            </div>

            {/* Cards Grid - Ultra Premium */}
            <div className="p-8">
              {loading ? (
                <div className="flex flex-col justify-center items-center py-24">
                  <div className={`relative animate-spin rounded-full h-20 w-20 border-4 border-t-transparent ${
                    darkMode 
                      ? 'border-blue-500 shadow-lg shadow-blue-500/50' 
                      : 'border-purple-500 shadow-lg shadow-purple-500/30'
                  }`}>
                    <div className="absolute inset-0 rounded-full animate-pulse opacity-50"></div>
                  </div>
                  <p className={`mt-6 text-sm font-bold ${
                    darkMode ? 'text-blue-400' : 'text-purple-600'
                  }`}>
                    Loading parameters...
                  </p>
                </div>
              ) : !parameters || parameters.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-24">
                  <div className={`p-8 rounded-3xl mb-6 shadow-2xl ${
                    darkMode 
                      ? 'bg-gradient-to-br from-blue-900/30 to-purple-900/30 shadow-blue-500/20' 
                      : 'bg-gradient-to-br from-purple-100 to-pink-100 shadow-purple-500/20'
                  }`}>
                    <FaBoxOpen className={`text-6xl ${
                      darkMode ? 'text-blue-400' : 'text-purple-500'
                    }`} />
                  </div>
                  <h3 className={`text-2xl font-extrabold mb-3 ${
                    darkMode 
                      ? 'text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400' 
                      : 'text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600'
                  }`}>
                    {searchTerm ? '🔍 No parameters found' : '🎯 No parameters yet'}
                  </h3>
                  <p className={`text-sm mb-8 font-medium ${
                    darkMode ? 'text-slate-400' : 'text-purple-600/70'
                  }`}>
                    {searchTerm 
                      ? 'Try adjusting your search terms or filters' 
                      : 'Get started by creating your first calculation parameter'}
                  </p>
                  {!searchTerm && (
                    <button
                      onClick={handleOpenModal}
                      className={`flex items-center gap-3 px-8 py-4 rounded-2xl font-bold transition-all duration-300 transform hover:scale-105 active:scale-95 shadow-xl hover:shadow-2xl ${
                        darkMode
                          ? 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white shadow-blue-500/50'
                          : 'bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white shadow-purple-500/30'
                      }`}
                    >
                      <FaPlus className="text-lg" />
                      <span>Create First Parameter</span>
                    </button>
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {(parameters || []).map((param, index) => (
                    <div
                      key={param._id}
                      className={`card-hover rounded-2xl border-2 p-6 transition-all duration-500 shadow-lg ${
                        darkMode
                          ? 'bg-gradient-to-br from-slate-800/80 to-slate-900/90 border-slate-700/50 hover:border-blue-500/50 hover:shadow-blue-500/20 hover:shadow-xl'
                          : 'bg-gradient-to-br from-white to-purple-50/30 border-purple-200/60 hover:border-purple-400/50 hover:shadow-purple-500/20 hover:shadow-xl'
                      }`}
                      style={{
                        animation: `slideUp 0.5s ease-out ${index * 0.05}s backwards`
                      }}
                    >
                      {/* Card Header - Enhanced */}
                      <div className="flex items-start justify-between mb-5">
                        <div className="flex items-center gap-3">
                          <span className={`px-4 py-1.5 text-xs font-black rounded-xl shadow-md ${
                            darkMode
                              ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-blue-500/30'
                              : 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white shadow-blue-500/20'
                          }`}>
                            {param.id || 'N/A'}
                          </span>
                          <span className={`px-4 py-1.5 text-xs font-black rounded-full shadow-md ${
                            param.calculationType === 'pcs'
                              ? (darkMode 
                                  ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-purple-500/30' 
                                  : 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-purple-500/20')
                              : (darkMode 
                                  ? 'bg-gradient-to-r from-emerald-600 to-green-600 text-white shadow-green-500/30' 
                                  : 'bg-gradient-to-r from-emerald-500 to-green-500 text-white shadow-green-500/20')
                          }`}>
                            {param.calculationType === 'pcs' ? 'Pieces' : 'Box'}
                          </span>
                        </div>
                        <div className="flex items-center gap-3">
                          <button
                            onClick={() => handleEdit(param)}
                            className={`p-2.5 rounded-xl transition-all duration-300 transform hover:scale-125 active:scale-95 shadow-md ${
                              darkMode
                                ? 'text-blue-400 hover:bg-blue-900/50 hover:shadow-blue-500/50'
                                : 'text-blue-600 hover:bg-blue-100 hover:shadow-blue-500/30'
                            }`}
                          >
                            <FaEdit className="text-lg" />
                          </button>
                          <button
                            onClick={() => handleDelete(param._id)}
                            className={`p-2.5 rounded-xl transition-all duration-300 transform hover:scale-125 active:scale-95 shadow-md ${
                              darkMode
                                ? 'text-red-400 hover:bg-red-900/50 hover:shadow-red-500/50'
                                : 'text-red-600 hover:bg-red-100 hover:shadow-red-500/30'
                            }`}
                          >
                            <FaTrash className="text-lg" />
                          </button>
                        </div>
                      </div>

                      {/* Parameter Name - Prominent */}
                      <h3 className={`text-xl font-black mb-3 tracking-tight ${
                        darkMode 
                          ? 'text-white' 
                          : 'text-transparent bg-clip-text bg-gradient-to-r from-gray-800 to-gray-900'
                      }`}>
                        {param.name}
                      </h3>
                      
                      {/* Category Badge - Enhanced */}
                      <div className="mb-5">
                        <span className={`inline-flex items-center px-4 py-1.5 text-xs font-bold rounded-full shadow-md ${
                          darkMode
                            ? 'bg-gradient-to-r from-slate-700 to-slate-600 text-slate-200 shadow-slate-900/50'
                            : 'bg-gradient-to-r from-gray-200 to-gray-300 text-gray-800 shadow-gray-400/30'
                        }`}>
                          <span className={`w-2 h-2 rounded-full mr-2 ${
                            darkMode ? 'bg-blue-400' : 'bg-purple-500'
                          }`}></span>
                          {param.category?.name || 'Uncategorized'}
                        </span>
                      </div>

                      {/* Details Grid - Premium Style */}
                      <div className={`grid grid-cols-2 gap-4 p-5 rounded-2xl border ${
                        darkMode 
                          ? 'bg-slate-950/50 border-slate-700/50' 
                          : 'bg-gradient-to-br from-purple-50/50 to-pink-50/50 border-purple-200/50'
                      }`}>
                        <div className="space-y-1.5">
                          <p className={`text-xs font-black uppercase tracking-wider ${
                            darkMode ? 'text-blue-400' : 'text-purple-600'
                          }`}>
                            📏 Dimensions
                          </p>
                          <p className={`text-sm font-extrabold ${
                            darkMode ? 'text-white' : 'text-gray-900'
                          }`}>
                            {param.width && param.height ? `${param.width} × ${param.height} mm` : '-'}
                          </p>
                        </div>
                        <div className="space-y-1.5">
                          <p className={`text-xs font-black uppercase tracking-wider ${
                            darkMode ? 'text-purple-400' : 'text-pink-600'
                          }`}>
                            📦 Pieces/Box
                          </p>
                          <p className={`text-sm font-extrabold ${
                            darkMode ? 'text-white' : 'text-gray-900'
                          }`}>
                            {param.piecesPerBox || '-'}
                          </p>
                        </div>
                        <div className="space-y-1.5">
                          <p className={`text-xs font-black uppercase tracking-wider ${
                            darkMode ? 'text-green-400' : 'text-emerald-600'
                          }`}>
                            ⚖️ Weight
                          </p>
                          <p className={`text-sm font-extrabold ${
                            darkMode ? 'text-white' : 'text-gray-900'
                          }`}>
                            {param.weightPerBox ? `${param.weightPerBox} kg` : '-'}
                          </p>
                        </div>
                        <div className="space-y-1.5">
                          <p className={`text-xs font-black uppercase tracking-wider ${
                            darkMode ? 'text-orange-400' : 'text-orange-600'
                          }`}>
                            📐 Coverage
                          </p>
                          <p className={`text-sm font-extrabold ${
                            darkMode ? 'text-white' : 'text-gray-900'
                          }`}>
                            {param.coverageArea 
                              ? `${param.coverageArea} ${param.coverageAreaUnit || 'sqft'}`
                              : '-'
                            }
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Pagination Controls - Ultra Stylish */}
          {total > limit && parameters && parameters.length > 0 && (
            <div className={`relative overflow-hidden border rounded-3xl shadow-2xl px-8 py-6 mt-8 transition-all duration-500 ${
              darkMode 
                ? 'bg-gradient-to-r from-slate-900/95 via-slate-800/90 to-slate-900/95 border-slate-700/50 shadow-blue-900/20' 
                : 'bg-white/90 backdrop-blur-xl border-purple-200/60 shadow-purple-200/30'
            }`}>
              <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                <div className={`flex items-center text-sm font-bold ${darkMode ? 'text-blue-300' : 'text-purple-700'}`}>
                  Showing{' '}
                  <span className={`mx-2 px-3 py-1 rounded-lg font-black ${
                    darkMode 
                      ? 'bg-blue-600/30 text-blue-400' 
                      : 'bg-purple-100 text-purple-700'
                  }`}>
                    {((page - 1) * limit) + 1}
                  </span>
                  to{' '}
                  <span className={`mx-2 px-3 py-1 rounded-lg font-black ${
                    darkMode 
                      ? 'bg-blue-600/30 text-blue-400' 
                      : 'bg-purple-100 text-purple-700'
                  }`}>
                    {Math.min(page * limit, total)}
                  </span>
                  of{' '}
                  <span className={`mx-2 px-3 py-1 rounded-lg font-black ${
                    darkMode 
                      ? 'bg-purple-600/30 text-purple-400' 
                      : 'bg-pink-100 text-pink-700'
                  }`}>
                    {total}
                  </span>
                  parameters
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setPage(Math.max(1, page - 1))}
                    disabled={page === 1}
                    className={`px-6 py-3 text-sm font-black rounded-xl transition-all duration-300 transform hover:scale-105 active:scale-95 shadow-lg disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100 ${
                      darkMode
                        ? 'bg-gradient-to-r from-slate-700 to-slate-600 border border-slate-600 text-blue-300 hover:from-slate-600 hover:to-slate-500 shadow-slate-900/50'
                        : 'bg-gradient-to-r from-purple-100 to-pink-100 border border-purple-200 text-purple-700 hover:from-purple-200 hover:to-pink-200 shadow-purple-500/20'
                    }`}
                  >
                    ← Previous
                  </button>
                  
                  {/* Page Numbers - Enhanced */}
                  <div className="flex gap-2">
                    {(() => {
                      const totalPages = Math.ceil(total / limit);
                      const pages = [];
                      const startPage = Math.max(1, page - 2);
                      const endPage = Math.min(totalPages, page + 2);
                      
                      for (let i = startPage; i <= endPage; i++) {
                        pages.push(
                          <button
                            key={i}
                            onClick={() => setPage(i)}
                            className={`w-12 h-12 text-sm font-black rounded-xl transition-all duration-300 shadow-lg ${
                              i === page
                                ? (darkMode 
                                    ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-blue-500/50 scale-110 ring-4 ring-blue-500/30' 
                                    : 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-purple-500/40 scale-110 ring-4 ring-purple-500/20')
                                : (darkMode
                                    ? 'text-blue-300 bg-slate-700/50 border border-slate-600 hover:bg-slate-600 hover:scale-105'
                                    : 'text-purple-600 bg-white border border-purple-200 hover:bg-purple-50 hover:scale-105')
                            }`}
                          >
                            {i}
                          </button>
                        );
                      }
                      return pages;
                    })()}
                  </div>
                  
                  <button
                    onClick={() => setPage(Math.min(Math.ceil(total / limit), page + 1))}
                    disabled={page >= Math.ceil(total / limit)}
                    className={`px-6 py-3 text-sm font-black rounded-xl transition-all duration-300 transform hover:scale-105 active:scale-95 shadow-lg disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100 ${
                      darkMode
                        ? 'bg-gradient-to-r from-slate-700 to-slate-600 border border-slate-600 text-blue-300 hover:from-slate-600 hover:to-slate-500 shadow-slate-900/50'
                        : 'bg-gradient-to-r from-purple-100 to-pink-100 border border-purple-200 text-purple-700 hover:from-purple-200 hover:to-pink-200 shadow-purple-500/20'
                    }`}
                  >
                    Next →
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Floating Add Button - Ultra Premium */}
        <button
          onClick={() => {
            resetForm();
            setShowParameterModal(true);
          }}
          className={`group fixed bottom-10 right-10 p-6 rounded-3xl shadow-2xl transition-all duration-500 transform hover:scale-110 active:scale-95 z-50 ${
            darkMode
              ? 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white shadow-blue-500/50 hover:shadow-purple-500/70'
              : 'bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white shadow-purple-500/40 hover:shadow-pink-500/60'
          }`}
          style={{
            animation: 'float 3s ease-in-out infinite'
          }}
        >
          <FaPlus className="text-3xl transition-transform duration-300 group-hover:rotate-90" />
          <div className={`absolute -top-1 -right-1 w-4 h-4 rounded-full animate-ping ${
            darkMode ? 'bg-blue-400' : 'bg-purple-400'
          }`}></div>
        </button>
      </div>

      {/* Parameter Modal - TODO: Add modal content here */}
      {showParameterModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className={`max-w-2xl w-full rounded-3xl shadow-2xl border-2 ${
            darkMode 
              ? 'bg-slate-900 border-slate-700' 
              : 'bg-white border-purple-200'
          }`}>
            {/* Modal Header */}
            <div className={`px-8 py-6 border-b flex items-center justify-between ${
              darkMode ? 'border-slate-700' : 'border-purple-200'
            }`}>
              <h2 className={`text-2xl font-extrabold ${
                darkMode 
                  ? 'text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400' 
                  : 'text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600'
              }`}>
                {editId ? '✏️ Edit Parameter' : '➕ Create Parameter'}
              </h2>
              <button
                onClick={() => setShowParameterModal(false)}
                className={`p-2 rounded-xl transition-all duration-300 hover:scale-110 ${
                  darkMode 
                    ? 'text-slate-400 hover:text-white hover:bg-slate-800' 
                    : 'text-gray-500 hover:text-gray-900 hover:bg-purple-100'
                }`}
              >
                <FaTimes className="text-2xl" />
              </button>
            </div>

            {/* Modal Body */}
            <form onSubmit={handleSubmit} className="p-8">
              <div className="space-y-6">
                {/* Name */}
                <div>
                  <label className={`block text-sm font-bold mb-2 ${
                    darkMode ? 'text-blue-300' : 'text-purple-700'
                  }`}>
                    Parameter Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className={`w-full px-4 py-3 rounded-xl border-2 font-medium transition-all focus:outline-none focus:ring-4 ${
                      darkMode
                        ? 'bg-slate-950/50 border-slate-700 text-white placeholder-slate-500 focus:border-blue-500 focus:ring-blue-500/30'
                        : 'bg-white border-purple-200 text-gray-900 placeholder-purple-400/70 focus:border-purple-500 focus:ring-purple-500/20'
                    }`}
                    placeholder="Enter parameter name"
                  />
                </div>

                {/* Category */}
                <div>
                  <label className={`block text-sm font-bold mb-2 ${
                    darkMode ? 'text-blue-300' : 'text-purple-700'
                  }`}>
                    Category *
                  </label>
                  <Select
                    options={categoryOptions}
                    value={categoryOptions && categoryOptions.find(opt => opt.value === formData.category)}
                    onChange={(selected) => setFormData({ ...formData, category: selected?.value || '' })}
                    styles={selectStyles}
                    placeholder="Select category"
                    isClearable
                  />
                </div>

                {/* Calculation Type */}
                <div>
                  <label className={`block text-sm font-bold mb-2 ${
                    darkMode ? 'text-blue-300' : 'text-purple-700'
                  }`}>
                    Calculation Type *
                  </label>
                  <Select
                    options={calculationTypeOptions}
                    value={calculationTypeOptions.find(opt => opt.value === formData.calculationType)}
                    onChange={(selected) => setFormData({ ...formData, calculationType: selected?.value || 'pcs' })}
                    styles={selectStyles}
                    placeholder="Select type"
                  />
                </div>

                {/* Dimensions Grid */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className={`block text-sm font-bold mb-2 ${
                      darkMode ? 'text-blue-300' : 'text-purple-700'
                    }`}>
                      Width (mm)
                    </label>
                    <input
                      type="number"
                      value={formData.width}
                      onChange={(e) => setFormData({ ...formData, width: e.target.value })}
                      className={`w-full px-4 py-3 rounded-xl border-2 font-medium transition-all focus:outline-none focus:ring-4 ${
                        darkMode
                          ? 'bg-slate-950/50 border-slate-700 text-white placeholder-slate-500 focus:border-blue-500 focus:ring-blue-500/30'
                          : 'bg-white border-purple-200 text-gray-900 placeholder-purple-400/70 focus:border-purple-500 focus:ring-purple-500/20'
                      }`}
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-bold mb-2 ${
                      darkMode ? 'text-blue-300' : 'text-purple-700'
                    }`}>
                      Height (mm)
                    </label>
                    <input
                      type="number"
                      value={formData.height}
                      onChange={(e) => setFormData({ ...formData, height: e.target.value })}
                      className={`w-full px-4 py-3 rounded-xl border-2 font-medium transition-all focus:outline-none focus:ring-4 ${
                        darkMode
                          ? 'bg-slate-950/50 border-slate-700 text-white placeholder-slate-500 focus:border-blue-500 focus:ring-blue-500/30'
                          : 'bg-white border-purple-200 text-gray-900 placeholder-purple-400/70 focus:border-purple-500 focus:ring-purple-500/20'
                      }`}
                      placeholder="0"
                    />
                  </div>
                </div>

                {/* Box Details Grid */}
                {formData.calculationType === 'pcsPerBox' && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className={`block text-sm font-bold mb-2 ${
                        darkMode ? 'text-blue-300' : 'text-purple-700'
                      }`}>
                        Pieces per Box
                      </label>
                      <input
                        type="number"
                        value={formData.piecesPerBox}
                        onChange={(e) => setFormData({ ...formData, piecesPerBox: e.target.value })}
                        className={`w-full px-4 py-3 rounded-xl border-2 font-medium transition-all focus:outline-none focus:ring-4 ${
                          darkMode
                            ? 'bg-slate-950/50 border-slate-700 text-white placeholder-slate-500 focus:border-blue-500 focus:ring-blue-500/30'
                            : 'bg-white border-purple-200 text-gray-900 placeholder-purple-400/70 focus:border-purple-500 focus:ring-purple-500/20'
                        }`}
                        placeholder="0"
                      />
                    </div>
                    <div>
                      <label className={`block text-sm font-bold mb-2 ${
                        darkMode ? 'text-blue-300' : 'text-purple-700'
                      }`}>
                        Weight per Box (kg)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        value={formData.weightPerBox}
                        onChange={(e) => setFormData({ ...formData, weightPerBox: e.target.value })}
                        className={`w-full px-4 py-3 rounded-xl border-2 font-medium transition-all focus:outline-none focus:ring-4 ${
                          darkMode
                            ? 'bg-slate-950/50 border-slate-700 text-white placeholder-slate-500 focus:border-blue-500 focus:ring-blue-500/30'
                            : 'bg-white border-purple-200 text-gray-900 placeholder-purple-400/70 focus:border-purple-500 focus:ring-purple-500/20'
                        }`}
                        placeholder="0.00"
                      />
                    </div>
                  </div>
                )}

                {/* Coverage Area */}
                <div>
                  <label className={`block text-sm font-bold mb-2 ${
                    darkMode ? 'text-blue-300' : 'text-purple-700'
                  }`}>
                    Coverage Area Unit
                  </label>
                  <Select
                    options={coverageAreaUnitOptions}
                    value={coverageAreaUnitOptions.find(opt => opt.value === formData.coverageAreaUnit)}
                    onChange={(selected) => setFormData({ ...formData, coverageAreaUnit: selected?.value || 'sqft' })}
                    styles={selectStyles}
                    placeholder="Select unit"
                  />
                  {formData.coverageArea && (
                    <div className={`mt-3 p-4 rounded-xl border-2 ${
                      darkMode 
                        ? 'bg-blue-900/30 border-blue-700/50 text-blue-300' 
                        : 'bg-blue-50 border-blue-200 text-blue-700'
                    }`}>
                      <p className="text-sm font-bold">
                        📐 Auto-calculated: {formData.coverageArea} {formData.coverageAreaUnit}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Modal Footer */}
              <div className="flex justify-end gap-4 mt-8">
                <button
                  type="button"
                  onClick={() => setShowParameterModal(false)}
                  className={`px-6 py-3 rounded-xl font-bold transition-all duration-300 hover:scale-105 ${
                    darkMode
                      ? 'bg-slate-700 text-slate-200 hover:bg-slate-600'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className={`px-8 py-3 rounded-xl font-bold transition-all duration-300 hover:scale-105 shadow-lg disabled:opacity-50 ${
                    darkMode
                      ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-500 hover:to-purple-500 shadow-blue-500/50'
                      : 'bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:from-purple-600 hover:to-pink-600 shadow-purple-500/30'
                  }`}
                >
                  {loading ? 'Saving...' : (editId ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
